import { CURRENCY } from 'constants.js';
import { CORPORATE_CLIENT_COUNTRIES } from 'constants/corporateClients';

export const WALLET_NAME_MIN_LENGTH = 4;
export const WALLET_NAME_MAX_LENGTH = 30;

export const WALLET_STATUS = {
  active: 'active',
  frozen: 'frozen',
  closed: 'closed',
};

export const WALLET_TRANSACTION_TYPES = {
  parkingSession: 'parkingsession',
  parkingRefunded: 'parkingrefunded',
  topUpByCard: 'topupbycard',
  topUpByTransfer: 'topupbytransfer',
};

// Transactions that add to the wallet balance; the history shows their amount with a "+".
export const WALLET_CREDIT_TRANSACTION_TYPES = [
  WALLET_TRANSACTION_TYPES.topUpByCard,
  WALLET_TRANSACTION_TYPES.topUpByTransfer,
  WALLET_TRANSACTION_TYPES.parkingRefunded,
];

// Transactions that take from the wallet balance; the history shows their amount with a "-".
export const WALLET_DEBIT_TRANSACTION_TYPES = [WALLET_TRANSACTION_TYPES.parkingSession];

// `lastTopUp.paymentMethod` arrives as a display string ('Card', 'Bank Transfer'); these are the
// values it normalises to, and the suffixes of its corporateAccounts.wallet.dashboard.lastTopUp
// .paymentMethod.* translation keys.
export const WALLET_TOP_UP_PAYMENT_METHODS = {
  card: 'card',
  bankTransfer: 'banktransfer',
};

export const WALLET_TRANSACTION_STATUSES = {
  completed: 'completed',
  rejected: 'rejected',
  pending: 'pending',
};

export const WALLET_CURRENCY_SYMBOLS = {
  gbp: CURRENCY.GB,
  eur: CURRENCY.FR,
};

export const LOW_BALANCE_ALERT_PRESETS = [100, 500, 1000];
export const LOW_BALANCE_ALERT_DEFAULT = 500;

// Card top-up (POST .../card-load-operations). `CardLoadStatus` is a lowercase string on the wire
// (the swagger models it as int 1–4 — `CARD_LOAD_STATUS_BY_CODE` tolerates that too).
export const CARD_LOAD_STATUS = {
  actionRequired: 'actionrequired',
  processing: 'processing',
  credited: 'credited',
  failed: 'failed',
};

export const CARD_LOAD_STATUS_BY_CODE = {
  1: CARD_LOAD_STATUS.actionRequired,
  2: CARD_LOAD_STATUS.processing,
  3: CARD_LOAD_STATUS.credited,
  4: CARD_LOAD_STATUS.failed,
};

// Discriminator of the literals the card-load poller throws to keep react-query retrying
export const CARD_LOAD_POLL_RESULT = {
  pending: 'pending',
  failed: 'failed',
  // The read resolved but carried no operation, so the outcome is unknown and polling cannot go on
  unreadable: 'unreadable',
};

// A 3DS challenge can legitimately take minutes; with the 2s/4s/6s delay staircase this is ~7.5 min
export const CARD_LOAD_POLL_RETRIES = 90;

// Wallet Service caps a single card load at 5,000 (GBP or EUR); amounts are minor units
export const CARD_LOAD_MAX_AMOUNT_MINOR = 500000;

// Same steps as the low-balance-alert presets (FS-404)
export const CARD_LOAD_PRESETS_MAJOR = [500, 1000, 2000];
export const CARD_LOAD_DEFAULT_PRESET_MAJOR = 1000;

// Client-side failure code for a challenge document the browser could not decode. Carried in the
// same `failure.code` slot as the service's WLT codes so support sees it in the toast.
export const SCA_CHALLENGE_DECODE_FAILURE_CODE = 'PORTAL-SCA-DECODE';

// Wallet Service `failure.code` / ProblemDetails codes → translation key suffix under
// corporateAccounts.wallet.dashboard.addFunds.confirmCardModal.error.*
//
// Only authorization-stage codes may claim "your card has not been charged". Anything that fails
// after the card was authorized (the ledger refusing the credit, fulfilment errors) leaves a hold
// that is released later, so those map to the neutral `notCompleted` copy.
export const CARD_LOAD_FAILURE_CODES = {
  // CAG rejected the credit after the card was authorized; the authorization is voided
  'WLT-1013': 'notCompleted',
  'WLT-5030': 'amountTooHigh',
  'WLT-5031': 'paymentAccountRefused',
  // Issuer refused the charge at authorization (the job read is the only signal for it)
  'WLT-5032': 'cardDeclined',
  'WLT-5033': 'conflict',
  'WLT-5034': 'walletNotActive',
  'WLT-5035': 'walletNotReady',
  // Fulfilment failed after capture; JMS voids/refunds
  'WLT-5040': 'notCompleted',
  'WLT-5041': 'notCompleted',
  'WLT-5043': 'notCompleted',
  [SCA_CHALLENGE_DECODE_FAILURE_CODE]: 'challengeUnavailable',
};

export const CARD_LOAD_ERROR_KEYS = {
  generic: 'generic',
  notCompleted: 'notCompleted',
  invalidRequest: 'invalidRequest',
  conflict: 'conflict',
  // FS-392: only primary_fleet_manager / fleet_manager on their own account may top up
  forbidden: 'forbidden',
  rejected: 'rejected',
  pollingExpired: 'pollingExpired',
};

const WALLET_TERMS_GB_URL = '/legal/wallet-terms-gb-2026-09-20.pdf';
const WALLET_TERMS_FR_URL = '/legal/wallet-terms-fr-2026-09-20.pdf';

export const WALLET_TERMS_AND_CONDITIONS_LINKS = {
  [CORPORATE_CLIENT_COUNTRIES.GB]: WALLET_TERMS_GB_URL,
  [CORPORATE_CLIENT_COUNTRIES.FR]: WALLET_TERMS_FR_URL,
  default: WALLET_TERMS_GB_URL,
};

// Placeholder — the real region-based wallet support phone numbers are TBD from product
export const WALLET_SUPPORT_PHONE_NUMBERS = {
  default: '8888888888',
};
